import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  submitVendorForApproval,
  approveVendor,
  rejectVendor,
  suspendVendor,
  blacklistVendor,
  reactivateVendor,
  isVendorActive,
} from '../api/vendor';
import { ACTION_SUCCESS_MESSAGES } from '../utils/vendorWorkflow';
import { vendorSubmitBlockReason } from '../utils/vendorEntity';
import { INV_ACTIONS } from '../utils/inventoryRoles';
import useInvRole from './useInvRole';

/** Vendor workflow action key → the permission it needs. */
const VENDOR_ACTION_PERMISSION = {
  submit: INV_ACTIONS.VENDOR_SUBMIT,
  approve: INV_ACTIONS.VENDOR_APPROVE,
  reject: INV_ACTIONS.VENDOR_REJECT,
  suspend: INV_ACTIONS.VENDOR_SUSPEND,
  blacklist: INV_ACTIONS.VENDOR_BLACKLIST,
  reactivate: INV_ACTIONS.VENDOR_REACTIVATE,
};

async function runVendorAction(vendorId, actionKey, opts = {}) {
  const body = { reason: opts.reason, remarks: opts.reason || opts.remarks };
  switch (actionKey) {
    case 'submit':
      return submitVendorForApproval(vendorId, body);
    case 'approve':
      return approveVendor(vendorId, body);
    case 'reject':
      return rejectVendor(vendorId, body);
    case 'suspend':
      return suspendVendor(vendorId, body);
    case 'blacklist':
      return blacklistVendor(vendorId, body);
    case 'reactivate':
      return reactivateVendor(vendorId, body);
    default:
      throw new Error(`Unknown vendor action: ${actionKey}`);
  }
}

export default function useVendorWorkflow(vendorId, { onSuccess, vendor } = {}) {
  const [loading, setLoading] = useState(false);
  const [activeAction, setActiveAction] = useState(null);
  const { can } = useInvRole();

  const executeAction = useCallback(async (actionKey, opts = {}) => {
    if (!vendorId || !actionKey) return null;
    const required = VENDOR_ACTION_PERMISSION[actionKey];
    if (required && !can(required)) return null;
    if (actionKey === 'submit' && vendor && !isVendorActive(vendor)) {
      const msg = 'Activate the vendor before submitting for approval.';
      toast.error(msg);
      throw new Error(msg);
    }
    if (actionKey === 'submit' && vendor) {
      const msg = vendorSubmitBlockReason(vendor);
      if (msg) {
        toast.error(msg);
        throw new Error(msg);
      }
    }

    setLoading(true);
    setActiveAction(actionKey);
    try {
      const result = await runVendorAction(vendorId, actionKey, opts);
      const message = ACTION_SUCCESS_MESSAGES[actionKey] || 'Action completed successfully.';
      toast.success(message);
      onSuccess?.(result, actionKey);
      return result;
    } catch (e) {
      toast.error(e.message || 'Action failed. Please try again.');
      throw e;
    } finally {
      setLoading(false);
      setActiveAction(null);
    }
  }, [vendorId, vendor, onSuccess, can]);

  return { executeAction, loading, activeAction };
}
